import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Button, Space, Alert, Typography, Tooltip, Input, Empty } from "antd";
import {
  WifiOutlined,
  ReloadOutlined,
  SettingOutlined,
  ExportOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageHeader from "./common/PageHeader";
import { discoverCameras } from "../network/api";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";

const { Text, Paragraph } = Typography;

// "Find Edge360 cameras on this network" (EDGE360-DEVICE-DISCOVERY-PLAN §4C).
// The server matches cameras by the public IP this request arrives from — the
// same WAN IP the devices heartbeat from — so no LAN scan / device-id is needed.
export default function DiscoverCameras() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ssid, setSsid] = useState("");
  const [result, setResult] = useState(null); // { publicIp, windowMins, data }

  const load = useCallback(async (ssidArg) => {
    setLoading(true);
    try {
      const res = await discoverCameras(ssidArg);
      setResult(res);
    } catch (e) {
      notify.error(serverError(e, "Discovery failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      title: "Device",
      key: "device",
      render: (_, r) => (
        <span>
          <Text strong>{r.deviceTitle || "Unnamed device"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>#{r.deviceId}</Text>
        </span>
      ),
    },
    {
      title: "Network (SSID)",
      dataIndex: "currentNetwork",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Local IP",
      dataIndex: "currentIp",
      // Clickable as a fallback when .local (mDNS) doesn't resolve (e.g. some
      // Android browsers) — opens the portal by raw IP instead.
      render: (v) =>
        v ? (
          <Tooltip title={`Open by IP — http://${v}/`}>
            <a href={`http://${v}/`} target="_blank" rel="noreferrer"><Text code>{v}</Text></a>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Last seen",
      dataIndex: "lastSeenAt",
      render: (v) => (v ? relativeTime(v) : "—"),
    },
    {
      title: "Outlet",
      key: "outlet",
      render: (_, r) =>
        r.shop ? (
          <span>
            <Text>{r.shop.code}</Text>
            {r.shop.title ? <Text type="secondary"> — {r.shop.title}</Text> : null}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => (
        <Space size={4} wrap>
          {r.online && <Tag color="green">online</Tag>}
          <Tag color={r.registered ? "blue" : "orange"}>{r.registered ? "registered" : "unregistered"}</Tag>
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<SettingOutlined />} onClick={() => navigate(`/dashboard/device-details/${r._id}`)}>
            Manage
          </Button>
          <Tooltip title={`Opens http://edge360-${r.deviceId}.local/ — needs same shop Wi-Fi (mDNS). If it doesn't resolve (some Android browsers), click the Local IP instead.`}>
            <Button
              size="small"
              icon={<ExportOutlined />}
              href={`http://edge360-${r.deviceId}.local/`}
              target="_blank"
              rel="noreferrer"
            >
              Portal
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const cams = result?.data || [];

  return (
    <div>
      <PageHeader
        title="Find Edge360 Cameras"
        subtitle="Cameras on your current shop Wi-Fi"
        icon={<WifiOutlined />}
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => load(ssid || undefined)} loading={loading}>
            Refresh
          </Button>
        }
      />

      {result && (
        <Alert
          type="info"
          showIcon
          icon={<GlobalOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <span>
              Your network's public IP: <Text code>{result.publicIp || "unknown"}</Text>
              <Text type="secondary"> · showing devices seen in the last {result.windowMins} min</Text>
            </span>
          }
          description="Devices are matched by the public IP you and they share on the shop Wi-Fi. If you see nothing, make sure you're on the shop Wi-Fi (not mobile data or a VPN)."
        />
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="Filter by SSID (optional)"
          style={{ width: 240 }}
          value={ssid}
          onChange={(e) => setSsid(e.target.value)}
          onPressEnter={() => load(ssid || undefined)}
          prefix={<WifiOutlined style={{ color: "#9aa5b8" }} />}
        />
        <Button onClick={() => load(ssid || undefined)} loading={loading}>Apply</Button>
        {ssid && (
          <Button type="text" onClick={() => { setSsid(""); load(); }}>Clear</Button>
        )}
      </Space>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={cams}
        loading={loading}
        pagination={false}
        scroll={{ x: 800 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Paragraph type="secondary" style={{ maxWidth: 420, margin: "8px auto" }}>
                  No cameras found on this network. Make sure you're on the <b>shop Wi-Fi</b>
                  {" "}(not mobile data / VPN) and the device has been powered on for a minute.
                </Paragraph>
              }
            />
          ),
        }}
      />
    </div>
  );
}
